import { useCallback } from 'react'
import { parsePath, useLocation } from 'react-router-dom'

import { mergeShellSearch } from '@/lib/shellSearch'

/**
 * Sets or deletes a URL search parameter based on the presence of a value.
 *
 * @param params The URLSearchParams object to modify.
 * @param key The key of the search parameter.
 * @param value The value of the search parameter. If undefined, the parameter will be deleted.
 */
export function setOrDeleteParam(
  params: URLSearchParams,
  key: string,
  value?: string
): void {
  if (value) params.set(key, value)
  else params.delete(key)
}

/**
 * Href builder for a cross-task link — the `useAppNavigate().toTask` intent in
 * anchor form. The result carries the target's own query plus the shell keys of
 * the current URL; the current tab's task query stays behind.
 *
 * Shares the parsePath + mergeShellSearch assembly with useAppNavigate's
 * toTask — same intent, anchor form instead of an imperative navigate.
 *
 * @returns A function that takes an href-shaped string and returns the built href.
 */
export function useShellHref() {
  const location = useLocation()

  return useCallback(
    (to: string) => {
      const path = parsePath(to)
      // Bare link to the page we are already on (a sidebar item for the active
      // tab): hand back the current URL unchanged so the click is a no-op.
      // Rebuilding it would strip the tab's task query and reset the view.
      // A target that brings its own query or hash still wins.
      if (path.pathname === location.pathname && !path.search && !path.hash) {
        return location.pathname + location.search + location.hash
      }
      return (
        (path.pathname ?? '') +
        mergeShellSearch(path.search ?? '', location.search) +
        (path.hash ?? '')
      )
    },
    [location.pathname, location.search, location.hash]
  )
}
